import React, {ReactElement} from "react"
import moment from "moment"
import {startCase} from "lodash"
import classNames from "classnames"
import {
	AllowedUnifiedApplicationsColumns,
	UnifiedApplication,
	UnifiedApplicationChildType,
	UnifiedApplicationsColumns,
	UnifiedApplicationStatus,
} from "../../resources/unifiedApplication"
import {useNavigateWithListPath} from "../../hooks/useListReturnPath"
import {DataTableCell, DataTableRow} from "../DataTable/DataTable"
import IdLink from "../IdLink/IdLink"
import Icon from "../Icon/Icon"
import Tag from "../Tag/Tag"
import {unifiedApplicationStatusTheme} from "../Tag"

interface UnifiedApplicationRowProps {
	unifiedApplication: UnifiedApplication
	orgNames: Map<string, string>
	includedColumns: Array<AllowedUnifiedApplicationsColumns>
}

function StatusTag({status}: {status: UnifiedApplicationStatus}) {
	return (
		<Tag size="large" variant="outline" theme={unifiedApplicationStatusTheme(status)} content={startCase(status)} />
	)
}

export default function UnifiedApplicationRow({
	unifiedApplication,
	orgNames,
	includedColumns,
}: UnifiedApplicationRowProps) {
	const navigateWithListPath = useNavigateWithListPath()

	const {id, type, attributes, relationships} = unifiedApplication
	const orgId = relationships.org?.data.id
	const childStatus = (childType: UnifiedApplicationChildType) =>
		attributes.children.find((child) => child.attributes.type === childType)?.attributes.status

	const cells: Record<UnifiedApplicationsColumns, ReactElement> = {
		[UnifiedApplicationsColumns.id]: (
			<DataTableCell className={classNames("data-table-id-cell")}>
				<IdLink href={`/unified-applications/${id}`}>{id}</IdLink>
			</DataTableCell>
		),
		[UnifiedApplicationsColumns.type]: (
			<DataTableCell>
				<span role="img" aria-label={type === "businessUnifiedApplication" ? "Business" : "Sole proprietor"}>
					<Icon icon={type === "businessUnifiedApplication" ? "toolbox" : "user-geometric-action---users"} />
				</span>
			</DataTableCell>
		),
		[UnifiedApplicationsColumns.org]: <DataTableCell>{orgId ? orgNames.get(orgId) ?? orgId : "—"}</DataTableCell>,
		[UnifiedApplicationsColumns.contactEmail]: (
			<DataTableCell>
				<span className="unified-applications-table-email">{attributes.contactEmail ?? "—"}</span>
			</DataTableCell>
		),
		[UnifiedApplicationsColumns.status]: (
			<DataTableCell>
				<StatusTag status={attributes.status} />
			</DataTableCell>
		),
		[UnifiedApplicationsColumns.unitApplication]: (
			<DataTableCell>
				<ChildStatus status={childStatus(UnifiedApplicationChildType.Unit)} />
			</DataTableCell>
		),
		[UnifiedApplicationsColumns.processorApplication]: (
			<DataTableCell>
				<ChildStatus status={childStatus(UnifiedApplicationChildType.Stripe)} />
			</DataTableCell>
		),
		[UnifiedApplicationsColumns.createdAt]: (
			<DataTableCell>{moment(attributes.createdAt).format("L LT")}</DataTableCell>
		),
	}

	return (
		<DataTableRow
			onClick={(e) => {
				e.preventDefault()
				navigateWithListPath(`/unified-applications/${id}`)
			}}
		>
			{includedColumns.map((column) => React.cloneElement(cells[column], {key: column}))}
		</DataTableRow>
	)
}

function ChildStatus({status}: {status?: UnifiedApplicationStatus}) {
	return status ? <StatusTag status={status} /> : <>—</>
}
